import { useState, useEffect } from 'react'
import { X, Search, Plus, Trash2, User, Shield, Edit, Eye } from 'lucide-react'
import { useQuery, useQueryClient } from 'react-query'
import { permissionService, PermissionLevel, Visibility, AgentPermissionsInfo } from '../services/api'
import { useAuthStore } from '../stores/authStore'

interface AgentPermissionsModalProps {
  agentId: string
  isOpen: boolean
  onClose: () => void
}

export function AgentPermissionsModal({ agentId, isOpen, onClose }: AgentPermissionsModalProps) {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedVisibility, setSelectedVisibility] = useState<Visibility>('private')

  const { data: permissionsInfo, isLoading, refetch } = useQuery<AgentPermissionsInfo>(
    ['agent-permissions', agentId],
    () => permissionService.getAgentPermissions(agentId),
    {
      enabled: isOpen && !!agentId,
      onSuccess: (data) => {
        setSelectedVisibility(data.visibility)
      }
    }
  )

  const handleSearchUsers = async () => {
    if (searchTerm.length < 2) {
      setSearchResults([])
      return
    }
    setIsSearching(true)
    try {
      const results = await permissionService.searchUsers(searchTerm)
      // * Filter out users already in permissions
      const existingUserIds = new Set([
        permissionsInfo?.ownerId,
        ...(permissionsInfo?.permissions.map(p => p.userId) || [])
      ])
      setSearchResults(results.filter(u => !existingUserIds.has(u.id)))
    } catch (error) {
      console.error('Erreur lors de la recherche:', error)
    } finally {
      setIsSearching(false)
    }
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearchUsers()
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  const handleAddCollaborator = async (userId: string) => {
    try {
      await permissionService.setPermission(agentId, userId, 'reader')
      setSearchTerm('')
      setSearchResults([])
      refetch()
      queryClient.invalidateQueries('agents')
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error)
      alert('Erreur lors de l\'ajout du collaborateur')
    }
  }

  const handleUpdatePermission = async (userId: string, level: PermissionLevel) => {
    try {
      if (level === 'owner') {
        if (!confirm('Êtes-vous sûr de vouloir transférer la propriété ?\n\nVous perdrez vos droits de propriétaire et ne pourrez plus revenir en arrière.')) {
          return
        }
        await permissionService.transferOwnership(agentId, userId)
      } else {
        await permissionService.setPermission(agentId, userId, level)
      }
      refetch()
      queryClient.invalidateQueries('agents')
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error)
      alert('Erreur lors de la mise à jour de la permission')
    }
  }

  const handleRemovePermission = async (userId: string) => {
    try {
      await permissionService.removePermission(agentId, userId)
      refetch()
      queryClient.invalidateQueries('agents')
    } catch (error) {
      console.error('Erreur lors de la suppression:', error)
      alert('Erreur lors de la suppression de la permission')
    }
  }

  const handleVisibilityChange = async (visibility: Visibility) => {
    try {
      await permissionService.setVisibility(agentId, visibility)
      setSelectedVisibility(visibility)
      queryClient.invalidateQueries('agents')
    } catch (error) {
      console.error('Erreur lors du changement de visibilité:', error)
      alert('Erreur lors du changement de visibilité')
    }
  }

  if (!isOpen) return null

  const isPrivate = selectedVisibility === 'private'
  const currentUserId = user?.id
  const isAdmin = user?.role === 'admin'
  const canEdit = permissionsInfo?.ownerId === currentUserId || isAdmin

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="studio-dialog bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden flex flex-col modal-content">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 modal-header">
          <h2 className="text-xl font-bold text-primary">Gestion des droits d'accès</h2>
          <button
            onClick={onClose}
            className="action-button action-button--tertiary"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 spinner"></div>
            </div>
          ) : (
            <>
              {/* Visibilité */}
              <div>
                <h3 className="text-lg font-medium mb-4 text-primary">Visibilité</h3>
                <div className="space-y-3">
                  <label className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer ${isPrivate ? 'modal-section-active' : 'modal-section'}`}>
                    <input
                      type="radio"
                      name="visibility"
                      value="private"
                      checked={selectedVisibility === 'private'}
                      onChange={() => handleVisibilityChange('private')}
                      disabled={!canEdit}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-primary">Privé</div>
                      <div className="text-sm text-secondary">Visible uniquement par le propriétaire</div>
                    </div>
                  </label>

                  <label className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer ${selectedVisibility === 'shared' ? 'modal-section-active' : 'modal-section'}`}>
                    <input
                      type="radio"
                      name="visibility"
                      value="shared"
                      checked={selectedVisibility === 'shared'}
                      onChange={() => handleVisibilityChange('shared')}
                      disabled={!canEdit}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-primary">Partagé</div>
                      <div className="text-sm text-secondary">Visible par le propriétaire et les collaborateurs</div>
                    </div>
                  </label>

                  <label className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer ${selectedVisibility === 'public' ? 'modal-section-active' : 'modal-section'}`}>
                    <input
                      type="radio"
                      name="visibility"
                      value="public"
                      checked={selectedVisibility === 'public'}
                      onChange={() => handleVisibilityChange('public')}
                      disabled={!canEdit}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-primary">Public</div>
                      <div className="text-sm text-secondary">Découvrable et accessible par tous</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Collaborateurs */}
              <div>
                <h3 className="text-lg font-medium mb-4 text-primary">Collaborateurs</h3>
                
                {/* Propriétaire */}
                <div className="mb-4 p-3 rounded-lg modal-section">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Shield className="w-5 h-5 text-robot-blue" />
                      <div>
                        <div className="font-medium text-primary">
                          {permissionsInfo?.ownerUsername || 'Propriétaire'}
                        </div>
                        <div className="text-sm text-secondary">Propriétaire</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Liste des collaborateurs */}
                {permissionsInfo?.permissions.map((permission) => (
                  <div key={permission.id} className="mb-2 p-3 rounded-lg flex items-center justify-between modal-section">
                    <div className="flex items-center space-x-3 flex-1">
                      <User className="w-5 h-5 text-secondary" />
                      <div className="flex-1">
                        <div className="font-medium text-primary">
                          {permission.username || permission.email || 'Utilisateur'}
                        </div>
                        <div className="text-sm text-secondary">
                          {permission.email}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <select id={`permission-level-${permission.userId}`} name="permission-level"
                        value={permission.permissionLevel}
                        onChange={(e) => handleUpdatePermission(permission.userId, e.target.value as PermissionLevel)}
                        disabled={!canEdit}
                        className="px-3 py-1 rounded-lg text-sm form-input"
                      >
                        <option value="reader">Lecteur</option>
                        <option value="editor">Éditeur</option>
                        {canEdit && <option value="owner">Propriétaire</option>}
                      </select>
                      {canEdit && (
                        <button
                          onClick={() => handleRemovePermission(permission.userId)}
                          className="action-button action-button--danger"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Ajouter un collaborateur */}
                {!isPrivate && canEdit && (
                  <div className="mt-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 icon-tertiary" />
                      <input id="permission-user-search" name="permission-user-search"
                        type="text"
                        placeholder="Rechercher un utilisateur..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="form-input pl-10"
                      />
                    </div>
                    {searchResults.length > 0 && (
                      <div className="mt-2 border rounded-lg overflow-hidden border-bottom-light">
                        {searchResults.map(user => (
                          <div
                            key={user.id}
                            className="p-3 flex items-center justify-between cursor-pointer modal-section table-hover-row"
                            onClick={() => handleAddCollaborator(user.id)}
                          >
                            <div>
                              <div className="font-medium text-primary">{user.username}</div>
                              <div className="text-sm text-secondary">{user.email}</div>
                            </div>
                            <Plus className="w-5 h-5 text-robot-blue" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

